/**
 * Type-safe navigation utilities.
 *
 * Provides compile-time route validation and autocomplete support.
 * Use this for static routes that are known at compile time.
 *
 * For dynamic routes with parameters, use the base navigate() function directly.
 *
 * @example
 * ```tsx
 * const navigateTo = useNavigateTo();
 * navigateTo('/app/leasing/applications'); // ✅ Type-safe, autocomplete works
 * navigateTo('/app/invalid'); // ❌ TypeScript error
 * ```
 *
 * @module utils/typeSafeNavigation
 */

import { AppRoute } from '@repo/shared-types';
import { useNavigate as useNavigateBase } from '../routes';

/**
 * Hook that returns a type-safe navigation function.
 *
 * Use this for static routes that are defined in the AppRoute type.
 * For dynamic routes (e.g., with parameters), use the base navigate() function.
 *
 * **Benefits:**
 * - Compile-time route validation catches typos
 * - Autocomplete helps discover available routes
 * - Refactoring is safer (TypeScript will catch broken routes)
 *
 * **When to use:**
 * - Static routes known at compile time
 * - New code should prefer this pattern
 *
 * **When not to use:**
 * - Dynamic routes with parameters (use `navigate()` directly)
 * - Routes constructed at runtime
 *
 * @returns A function that accepts only valid AppRoute values
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const navigateTo = useNavigateTo();
 *
 *   // ✅ Good: Type-safe static route
 *   navigateTo('/app/leasing/applications');
 *
 *   // ❌ Error: Invalid route (TypeScript will catch this)
 *   // navigateTo('/app/invalid-route');
 *
 *   // For dynamic routes, use navigate() directly:
 *   const navigate = useNavigate();
 *   navigate(`/app/tenants/${tenantId}`);
 * };
 * ```
 */
export const useNavigateTo = () => {
  const navigate = useNavigateBase();
  return (route: AppRoute) => navigate(route);
};
